/**
 * API: ciclo de vida comercial self-service de la membresía (Sprint 25).
 *
 * Espejo del contrato de `backend/app/api/v1/membresia.py` — nombres de
 * campo snake_case idénticos a los DTOs del backend.
 *
 * La mayoría de los endpoints son autenticados (`admin_municipal` o
 * cualquier miembro del tenant, según el caso — ver el backend) y usan
 * `request()` de `_client.ts` igual que el resto de `lib/api/*`.
 *
 * `fetchVinculacion` y `pagarVinculacion` son la excepción: los llama un
 * contacto SIN sesión que abrió un enlace de vinculación pública, así
 * que no deben mandar `Authorization`. `request()` siempre intenta
 * adjuntar el token si hay una sesión de Supabase activa en el browser
 * (edge case: alguien abre el enlace en una pestaña donde ya está
 * logueado) — para no depender de ese detalle, estos dos usan
 * `requestPublic()`, una copia deliberadamente más simple de
 * `request()` sin lookup de token ni el side-effect de `signOut()` en
 * 401 (no aplica: acá no hay sesión que cerrar).
 */

import { apiErrorFromResponse, safeFetch } from "../errors";
import { supabase } from "../supabase";
import { API_V1, getAccessToken, request } from "./_client";

export type MembresiaTier = "esencial" | "pro";
export type MembresiaPeriod = "trimestral" | "semestral" | "annual";
export type EquipoRole = "formulador" | "colaborador";

// --- /membresia/renovar -------------------------------------------------

export interface RenovarMembresiaRequest {
  tier: MembresiaTier;
  period: MembresiaPeriod;
}

/** Subconjunto de `OnboardingContextDTO` — lo mínimo que cambia tras renovar/pagar. */
export interface MembresiaContextoDTO {
  tenant_status: string;
  subscription_state: string;
  tier: string;
  tier_period: string;
  /** Último día pagado tras el pago (`"YYYY-MM-DD"`, Colombia) — issue #734. */
  tier_valid_until: string;
}

/** Pago SIMULADO (Sprint 25 no integra pasarela real) — reactiva o renueva por anticipado. */
export function renovarMembresia(payload: RenovarMembresiaRequest): Promise<MembresiaContextoDTO> {
  return request<MembresiaContextoDTO>("/membresia/renovar", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

// --- /membresia/equipo ----------------------------------------------------

export interface EquipoMiembroDTO {
  membership_id: string;
  full_name: string;
  position: string | null;
  role: string;
  accepted_at: string | null;
  invited_at: string;
}

export interface EquipoDTO {
  miembros: EquipoMiembroDTO[];
  seats_used: number;
  seats_limit: number;
  tier: string;
}

/** Cualquier miembro activo del tenant puede leerlo (no solo admin_municipal). */
export function fetchEquipo(): Promise<EquipoDTO> {
  return request<EquipoDTO>("/membresia/equipo");
}

export interface InvitarEquipoRequest {
  email: string;
  role: EquipoRole;
}

/** Solo admin_municipal. 409 si el cupo del tier ya está lleno. */
export function invitarMiembro(payload: InvitarEquipoRequest): Promise<EquipoMiembroDTO> {
  return request<EquipoMiembroDTO>("/membresia/equipo/invitaciones", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/**
 * Reenvía el magic link de una invitación pendiente. 204 sin body — no
 * podemos usar `request<T>()` que asume JSON (mismo patrón que
 * `admin.ts::revokeAdminTenantMember`).
 */
export async function reenviarInvitacion(membershipId: string): Promise<void> {
  const token = await getAccessToken();
  const headers = new Headers({ Accept: "application/json" });
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await safeFetch(
    `${API_V1}/membresia/equipo/invitaciones/${encodeURIComponent(membershipId)}/reenviar`,
    { method: "POST", headers },
  );

  if (res.status === 401) {
    await supabase.auth.signOut();
  }
  if (!res.ok) {
    throw await apiErrorFromResponse(res);
  }
}

/**
 * Quita a un miembro del equipo (soft-revoke). 204 sin body — mismo
 * patrón que `reenviarInvitacion`/`admin.ts::revokeAdminTenantMember`.
 */
export async function quitarMiembro(membershipId: string): Promise<void> {
  const token = await getAccessToken();
  const headers = new Headers({ Accept: "application/json" });
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await safeFetch(
    `${API_V1}/membresia/equipo/miembros/${encodeURIComponent(membershipId)}`,
    { method: "DELETE", headers },
  );

  if (res.status === 401) {
    await supabase.auth.signOut();
  }
  if (!res.ok) {
    throw await apiErrorFromResponse(res);
  }
}

// --- /membresia/vinculacion/{token} — SIN auth ----------------------------

export interface VinculacionPublicaDTO {
  municipality_name: string;
  tenant_display_name: string;
  tier: string;
  period: string;
  paid: boolean;
  invitations_count: number;
  expires_at: string;
  estado_funnel: string;
}

export interface InvitacionResultadoDTO {
  email_masked: string;
  /**
   * `"enviada"` — se mando invitacion nueva; `"ya_era_miembro"` — el
   * contacto ya tenia membership activa, se salteo sin fallar;
   * `"promovido_a_admin"` — el contacto era el PRIMER invitado y ya
   * tenia membership activa, asi que esa membership se elevo a
   * `admin_municipal` en vez de crear una nueva (fix F13 del BE).
   */
  status: "enviada" | "ya_era_miembro" | "promovido_a_admin";
}

export interface VinculacionPagoDTO {
  paid: boolean;
  tenant_status: string;
  subscription_state: string;
  tier: string;
  tier_period: string;
  invitaciones: InvitacionResultadoDTO[];
}

/**
 * Wrapper de fetch para los 2 endpoints públicos — copia deliberadamente
 * simplificada de `request()` en `_client.ts`: sin token, sin
 * Authorization, sin el side-effect de `signOut()` en 401 (ver docstring
 * del módulo).
 */
async function requestPublic<T>(path: string, init?: Parameters<typeof fetch>[1]): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set("Accept", "application/json");

  const res = await safeFetch(`${API_V1}${path}`, { ...init, headers });

  if (!res.ok) {
    throw await apiErrorFromResponse(res);
  }

  return (await res.json()) as T;
}

/** Público — un contacto sin sesión consulta si su enlace de vinculación sirve. */
export function fetchVinculacion(token: string): Promise<VinculacionPublicaDTO> {
  return requestPublic<VinculacionPublicaDTO>(
    `/membresia/vinculacion/${encodeURIComponent(token)}`,
  );
}

/** Público — paga (simulado) y activa el municipio + invita a los contactos del enlace. */
export function pagarVinculacion(token: string): Promise<VinculacionPagoDTO> {
  return requestPublic<VinculacionPagoDTO>(
    `/membresia/vinculacion/${encodeURIComponent(token)}/pagar`,
    { method: "POST" },
  );
}
